import React, { useCallback, useEffect, useState } from "react";
import { authFetch } from "../../utils/auth";

const DAYS = [
  [1, "L"],
  [2, "M"],
  [3, "X"],
  [4, "J"],
  [5, "V"],
  [6, "S"],
  [7, "D"],
];
const LABELS = {
  daily: "Diaria",
  weekly: "Semanal",
  monthly: "Mensual",
  manual: "Manual",
};
const suggestedSchedule = (profile) =>
  profile.schedule_type ||
  (["daily", "weekly", "monthly"].includes(profile.inferred_schedule)
    ? profile.inferred_schedule
    : "manual");

export default function BackupProfilesReview({ clientId, onClose }) {
  const [profiles, setProfiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await authFetch(
        `/api/backup-profiles?client_id=${clientId}`,
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Error cargando perfiles");
      setProfiles(
        (data.profiles || []).map((profile) => ({
          ...profile,
          schedule_type: suggestedSchedule(profile),
          expected_time: profile.expected_time || "22:00",
          grace_minutes: profile.grace_minutes ?? 720,
        })),
      );
      setUsers(data.users || []);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);
  useEffect(() => {
    load();
  }, [load]);
  const patch = (id, values) =>
    setProfiles((items) =>
      items.map((item) => (item.id === id ? { ...item, ...values } : item)),
    );
  const toggleDay = (profile, day) => {
    const days = profile.expected_weekdays || [];
    patch(profile.id, {
      expected_weekdays: days.includes(day)
        ? days.filter((value) => value !== day)
        : [...days, day].sort(),
    });
  };
  const save = async (profile, monitoringState = "confirmed") => {
    setSaving(profile.id);
    setError("");
    try {
      const response = await authFetch(`/api/backup-profiles/${profile.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...profile, monitoring_state: monitoringState }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "No se pudo guardar");
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(null);
    }
  };
  return (
    <div
      className="bk-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Configurar perfiles de copia"
    >
      <div className="bk-modal-content bk-profile-modal">
        <div className="bk-modal-head">
          <div>
            <div className="bk-modal-title">Configurar perfiles de copia</div>
            <div className="bk-modal-sub">
              Las propuestas no generan alertas hasta confirmarlas.
            </div>
          </div>
          <button className="button" onClick={onClose}>
            ✕
          </button>
        </div>
        {error ? <div className="bk-error">{error}</div> : null}
        {loading ? (
          <div className="bk-muted">Cargando perfiles…</div>
        ) : profiles.length === 0 ? (
          <div className="bk-muted">
            No hay perfiles detectados para este cliente.
          </div>
        ) : (
          <div className="bk-profile-list">
            {profiles.map((profile) => (
              <article className="bk-profile-card" key={profile.id}>
                <div className="bk-profile-heading">
                  <div>
                    <strong>{profile.display_name}</strong>
                    <div className="bk-muted">
                      {profile.evidence_runs} ejecuciones · propuesta:{" "}
                      {LABELS[profile.inferred_schedule] ||
                        profile.inferred_schedule}
                    </div>
                  </div>
                  <span
                    className={`bk-pill ${profile.monitoring_state === "confirmed" ? "ok" : "neutral"}`}
                  >
                    {profile.monitoring_state === "confirmed"
                      ? "Confirmado"
                      : profile.monitoring_state === "archived"
                        ? "Archivado"
                        : "Pendiente"}
                  </span>
                </div>
                <div className="bk-profile-fields">
                  <label>
                    Nombre
                    <input
                      className="input"
                      value={profile.display_name}
                      onChange={(event) =>
                        patch(profile.id, { display_name: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    Frecuencia
                    <select
                      value={profile.schedule_type}
                      onChange={(event) =>
                        patch(profile.id, { schedule_type: event.target.value })
                      }
                    >
                      {Object.entries(LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Hora
                    <input
                      className="input"
                      type="time"
                      value={profile.expected_time}
                      onChange={(event) =>
                        patch(profile.id, { expected_time: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    Margen (min)
                    <input
                      className="input"
                      type="number"
                      min="0"
                      max="10080"
                      value={profile.grace_minutes}
                      onChange={(event) =>
                        patch(profile.id, {
                          grace_minutes: Number(event.target.value),
                        })
                      }
                    />
                  </label>
                  <label>
                    Responsable
                    <select
                      value={profile.responsible_user_id || ""}
                      onChange={(event) =>
                        patch(profile.id, {
                          responsible_user_id: event.target.value
                            ? Number(event.target.value)
                            : null,
                        })
                      }
                    >
                      <option value="">Sin asignar</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.display_name || user.email}
                        </option>
                      ))}
                    </select>
                  </label>
                  {profile.schedule_type === "monthly" ? (
                    <label>
                      Día del mes
                      <input
                        className="input"
                        type="number"
                        min="1"
                        max="31"
                        value={profile.expected_day_of_month || 1}
                        onChange={(event) =>
                          patch(profile.id, {
                            expected_day_of_month: Number(event.target.value),
                          })
                        }
                      />
                    </label>
                  ) : null}
                </div>
                {profile.schedule_type === "weekly" ? (
                  <div className="bk-weekdays">
                    {DAYS.map(([day, label]) => (
                      <button
                        type="button"
                        className={`pillBtn ${(profile.expected_weekdays || []).includes(day) ? "on" : ""}`}
                        key={day}
                        onClick={() => toggleDay(profile, day)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                ) : null}
                <div className="bk-profile-actions">
                  <button
                    className="button primary"
                    disabled={saving === profile.id}
                    onClick={() => save(profile)}
                  >
                    {saving === profile.id ? "Guardando…" : "Confirmar"}
                  </button>
                  <button
                    className="button secondary"
                    disabled={saving === profile.id}
                    onClick={() => save(profile, "archived")}
                  >
                    Archivar
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
        <div className="bk-modal-foot">
          <button className="button" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
